// music/shell.jsx — full-takeover music surface (shell + hero + strip).
//
// Started life as a single panel/src/music.jsx file in PR 1. Split into
// the music/ folder in PR 2 to make room for states.jsx (S0/S1/S2),
// primitives.jsx (Section, Carousel, tiles, etc.), and the Spotify /
// favourites data hooks. This file owns the structural skeleton —
// MusicShell + NowPlayingHero + NowPlayingStrip — and stays stable as
// the rest of the music surface fills in around it.
//
//   - MusicShell      — full-takeover surface, replaces AModal for music.
//   - NowPlayingHero  — cover + meta + transport + primary volume rocker.
//   - NowPlayingStrip — compact home-view continue-listening affordance.
//
// Volume entity routing (amp-primary for Pavilion / Rumpus, Sonos-primary
// elsewhere) mirrors AMediaModal's logic in direction-a.jsx so behaviour
// is identical until PR 4 lands the proper VolumeBar + Transport
// primitives and retires AMediaModal.
//
// Pool-extension secondary rocker, per-member sliders, Tune-balance
// disclosure, destinations chips, S0 carousels, search, S3 target picker,
// group editor, line-in picker, favourite dispatch, and edge states are
// all deferred to subsequent PRs per the brief's §13.

import * as React from 'react';
import { I, CoverArt } from '../shared.jsx';
import { A_FONT, ALabel, useImageBlobUrl, fs, TakeoverShell, BusyDot, Card } from '../direction-a.jsx';
import { RADIUS } from '../theme.js';
import { useSpotifyToken } from '../hooks/use-spotify-token.jsx';
import { S0, S1, S2, S3, SFavourites, SLibrary, SApple } from './states.jsx';
import { VolumeBar, Transport, MemberRow, DestChip, DestExplainer, Scrubber, SourceCover } from './primitives.jsx';
// Shared with TVShell's audio disclosure — one slider row, one look,
// wherever tone is adjusted.
import { AudioSliderRow } from '../tv/primitives.jsx';
import { createDispatch, roomIdForEntity, ensureAmpListening } from './dispatch.jsx';
import { groupTargets } from './volume-drag.js';
import { spotifyTrackIdFrom, planQueue, resolveMissing, saveToSpotify, saveToApple, defaultQueueName, APPLE_CAP } from './save-playlist.js';
import { keepGoingRequest, freshTracks } from './library-model.js';
import { fetchLyrics, lineAt } from './lyrics.js';

const { useEffect, useState, useRef } = React;

// ─────────────────────────────────────────────────────────────────────────
// Entity routing helpers — kept in lockstep with AMediaModal so primary
// volume targets the same device the user is used to.
// ─────────────────────────────────────────────────────────────────────────

// Primary Sonos entity for a room (NOT the amp). PR 3 dispatch needs
// this because play_media must go to a Sonos, not the DRX amp — even
// for rooms where the amp owns volume (Pavilion / Rumpus).
function primarySonosFor(room) {
  if (!room) return null;
  return ({
    office:         'media_player.office',
    pavilion:       'media_player.pavilion',
    rumpus:         'media_player.rumpus',
    master_bed:     'media_player.master_bed',
    master_ensuite: 'media_player.ensuite',
    pool:           'media_player.pool',
  })[room.id] || null;
}

function primaryEntityFor(room) {
  if (!room) return null;
  // Amp-primary rooms — the speakers feed an external amp, so amp volume
  // is what's audible. Mirrors direction-a.jsx AMediaModal lines 920-944.
  if (room.av) {
    if (room.id === 'pavilion') return 'media_player.drx_3_1';
    if (room.id === 'rumpus')   return 'media_player.drx_5';
  }
  // Sonos-primary rooms — Sonos IS the amp.
  return ({
    office:         'media_player.office',
    pavilion:       'media_player.pavilion',
    rumpus:         'media_player.rumpus',
    master_bed:     'media_player.master_bed',
    master_ensuite: 'media_player.ensuite',
    pool:           'media_player.pool',
  })[room.id] || null;
}

function primaryLabelFor(room) {
  if (!room) return '';
  if (room.av) {
    if (room.id === 'pavilion') return 'DRX-3.1';
    if (room.id === 'rumpus')   return 'DRX-5';
  }
  return room.sonos?.room || 'Sonos';
}

// House-wide "what's playing" — first room reporting sonos.playing wins.
// Predictable ordering: ROOMS array order in shared.jsx. Multi-zone
// summary across rooms is deferred to PR 2's NowPlayingStrip variant.
function findPlayingRoom(rooms) {
  return rooms.find(r => r.sonos && r.sonos.playing) || null;
}

// Resolve the entity that owns the physical line-in jack for a room
// currently sourcing line-in. In a multi-member group, the source is
// the coordinator (groupMembers[0]) — followers inherit it. For a
// single speaker, the room's own primary Sonos is the source. Returns
// null when the room has no Sonos. Used by the hero / strip cover to
// pick a specific glyph (vinyl for office_turntable, generic line-in
// otherwise).
function lineInSourceEntity(room) {
  const sonos = room?.sonos;
  if (!sonos) return null;
  const coord = sonos.groupMembers?.[0];
  if (coord) return coord;
  return primarySonosFor(room);
}

function sourceCoverKindFor(room) {
  if (room?.sonos?.source !== 'Line-in') return null;
  return lineInSourceEntity(room) === 'media_player.office_turntable'
    ? 'vinyl'
    : 'line-in';
}

// Sonos track strings are typically "Artist — Title" (em-dash). Split
// so we can render artist + title on separate lines.
function splitTrack(track) {
  if (!track) return { title: '', artist: '' };
  const idx = track.indexOf('—');
  if (idx === -1) return { title: track, artist: '' };
  return {
    artist: track.slice(0, idx).trim(),
    title:  track.slice(idx + 1).trim(),
  };
}

// ─────────────────────────────────────────────────────────────────────────
// NowPlayingStrip — compact strip for the home view.
//
// Renders nothing when no room is playing. Tap → opens MusicShell with
// that room as the focus.
//
// Supersedes the per-RoomCard SonosNowPlayingStrip conceptually (per
// design HANDOFF), but PR 1 leaves the in-card strip alone — both
// surfaces coexist until PR 4's broader refactor.
// ─────────────────────────────────────────────────────────────────────────

function NowPlayingStrip({ T, rooms, onOpen }) {
  const room = findPlayingRoom(rooms);
  // Hooks must be unconditional, so resolve cover before the early return.
  const cover = useImageBlobUrl(room?.sonos?.mediaImage || null);
  if (!room) return null;
  const s = room.sonos;
  const { title, artist } = splitTrack(s.track || '');

  return (
    <button onClick={() => onOpen(room.id)} style={{
      width: '100%', height: '100%', display: 'flex', alignItems: 'center', gap: 16,
      padding: '16px 20px',
      background: T.card, border: 'none', borderRadius: RADIUS.card, boxShadow: T.cardShadow,
      color: T.ink, cursor: 'pointer', textAlign: 'left',
      fontFamily: A_FONT.sans,
      transition: 'transform .15s, box-shadow .15s',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = T.shadow;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = T.cardShadow;
    }}>
      {cover
        ? <img src={cover} alt="" width={64} height={64}
               style={{ borderRadius: RADIUS.key, objectFit: 'cover', background: T.cardSoft, display: 'block', flexShrink: 0 }}/>
        : sourceCoverKindFor(room)
          ? <SourceCover T={T} kind={sourceCoverKindFor(room)} size={64} radius={RADIUS.key}/>
          : <CoverArt seed={s.track || room.id} size={64} radius={RADIUS.key} label={false}/>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), fontWeight: 500, color: T.inkSoft, letterSpacing: '.12em', textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Now playing · {room.name}
          </span>
        </div>
        <div style={{ fontFamily: A_FONT.serif, fontSize: 26, fontWeight: 500, color: T.ink, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {title || 'Playing'}
        </div>
        {artist && (
          <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {artist}
          </div>
        )}
      </div>
      <span style={{ display: 'inline-flex', gap: 2, alignItems: 'flex-end', height: 12, flexShrink: 0, marginRight: 4 }}>
        {[3,8,5,9].map((h,i) => (
          <span key={i} style={{ width: 2, height: h, background: T.live, borderRadius: 1, animation: `a-eq .9s ${i*.12}s infinite alternate ease-in-out` }}/>
        ))}
      </span>
      <span style={{ color: T.mute, display: 'flex', flexShrink: 0 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6"/></svg>
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// NowPlayingHero — visual lead inside MusicShell.
//
// PR 1 implementation reuses Slider + I icons from shared.jsx and
// matches AMediaModal's renderRocker pattern for the volume control.
// PR 4 supersedes this with VolumeBar + Transport primitives.
// ─────────────────────────────────────────────────────────────────────────

function NowPlayingHero({ T, room, viewport, hass, sonosCommand, sonosSpeakerCommand, mediaPlayer, poolExtension, speakers, fallbackImage, showTuneBalance = false, onOpenGroup, eqCommand }) {
  const sonos = room?.sonos;
  // Apple now-playing fallback art. HA's Sonos integration frequently omits
  // entity_picture for Apple library streams (and a stale dispatchedArt is
  // wrong on auto-advance), so when the current track is an Apple library item
  // and HA gives us nothing, look the cover up the same way the queue rows do
  // (apple-lib ?songs=, sharing the session appleArtCache). Absolute mzstatic
  // URL, so it needs no blob fetch. mediaImage still wins when HA does push art.
  const [appleHeroArt, setAppleHeroArt] = useState(null);
  // Which disclosure is open under the hero: 'balance' | 'eq' | null.
  // One at a time — two stacked slider stacks under the cover is more
  // than the hero can carry, and they answer different questions
  // (how loud is each speaker vs how does this room sound).
  const [disclosure, setDisclosure] = useState(null);
  useEffect(() => {
    const aid = appleTrackIdFrom(sonos?.mediaContentId);
    if (!aid || !hass?.auth) { setAppleHeroArt(null); return; }
    if (appleArtCache.has(aid)) { setAppleHeroArt(appleArtCache.get(aid) || null); return; }
    let cancelled = false;
    (async () => {
      let token;
      try {
        const auth = hass.auth;
        if (auth.expired) await auth.refreshAccessToken();
        token = auth.accessToken;
      } catch { return; }
      try {
        const res = await fetch(`/music-api/apple-lib?songs=${encodeURIComponent(aid)}`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if ((data.failed || []).includes(aid)) return; // transient — don't cache, retry
        const url = (data.arts || {})[aid] || null;
        appleArtCache.set(aid, url);
        if (!cancelled) setAppleHeroArt(url);
      } catch { /* leave HA art / gradient */ }
    })();
    return () => { cancelled = true; };
  }, [sonos?.mediaContentId, hass]);
  // Hooks unconditional; resolve cover even when there's no room.
  // Prefer HA's media_image (rich metadata for Spotify tracks/albums),
  // fall back to a recently-dispatched tile's thumbnail, then to the
  // Apple-looked-up cover. Radio favourites typically don't push
  // stream-side artwork to HA — without the fallback the hero would
  // show the procedural gradient even when we have the favourite's logo
  // in hand. HA-proxy paths need a blob fetch for auth; absolute URLs
  // render direct.
  const sourceUrl = sonos?.mediaImage || fallbackImage || appleHeroArt || null;
  const needsBlob = !!sourceUrl && sourceUrl.startsWith('/');
  const blobUrl = useImageBlobUrl(needsBlob ? sourceUrl : null);
  const cover = needsBlob ? blobUrl : sourceUrl;

  // PR 1 rendered a placeholder here when nothing was playing. PR 2
  // round A removes that — when there's no room, the hero simply
  // doesn't render, and S0 (rendered by MusicShell below) becomes the
  // primary content. Callers handle the null return cleanly.
  if (!room || !sonos) return null;

  const isPhone = viewport === 'phone';
  const coverSize = isPhone ? 140 : 180;
  const { title, artist } = splitTrack(sonos.track || '');
  // Lyrics only for a titled track with a duration (not radio, line-in or TV).
  const hasLyrics = !!title && !!artist && (sonos?.mediaDuration || 0) > 0;
  const primaryEntity = primaryEntityFor(room);
  const primaryLabel  = primaryLabelFor(room);
  const live   = primaryEntity ? mediaPlayer?.(primaryEntity) : null;
  const volume = live?.volume ?? sonos?.volume ?? 0;

  // Pool-extension secondary VolumeBar — only when this room is
  // currently extending to Pool via DRX-5 zone 3. Per §7 "Pool-extension
  // secondary volume": a second rocker appears under the primary,
  // visually bracketed (oak/accent left-border + indent). The pool
  // entity (drx_5_zone_3) is HA's per-extend volume control.
  const ourPoolActive = room.id === 'pavilion' ? poolExtension?.pavilionActive
                      : room.id === 'rumpus'   ? poolExtension?.rumpusActive
                      : false;
  const poolLive = ourPoolActive ? mediaPlayer?.('media_player.drx_5_zone_3') : null;

  // Group-volume slider — when the focused room's Sonos is in a
  // multi-member group, expose a single control that scales all
  // members together. Slider position = current group average;
  // onChange applies the *delta* to each member so relative balance
  // is preserved (matches Sonos's native group-volume semantics).
  // Per-member fine-tuning still lives inside TuneBalance below.
  const groupMembers = sonos?.groupMembers || [];
  const inGroup = groupMembers.length > 1;
  // Tone is offered only where the room's EQ owner is actually reachable.
  // Pool has no owner the panel can drive (Connect → optical → SR2-125,
  // which has no network control), so it gets no Sound pill rather than a
  // dead one. See overlayEq in hooks/use-panel-rooms.jsx.
  const hasEq = !!(room?.eq && (room.eq.bass || room.eq.treble) && eqCommand);
  const memberObjs = inGroup
    ? groupMembers.map((eid) => (speakers || []).find((sp) => sp.entity === eid)).filter(Boolean)
    : [];
  const memberVols = memberObjs.map((m) => m.volume ?? 0);
  const groupAvg = memberVols.length
    ? Math.round(memberVols.reduce((a, b) => a + b, 0) / memberVols.length)
    : 0;
  // Group volume — a delta on the group average, so per-member balance
  // is preserved. The delta is measured from a baseline captured once
  // per gesture, NOT from live state on every move.
  //
  // Why: the old version did `delta = newAvg - groupAvg` against the
  // current average and added it to each member's current volume. That
  // is only correct when the echo has caught up between one move and
  // the next, which under a drag it has not — so each move computed its
  // delta from a stale average and the group overshot. Throttling alone
  // would have made that less frequent, not correct. Against a fixed
  // baseline every send is absolute and idempotent, so re-sending the
  // same target is a no-op rather than another push in the same
  // direction. See music/volume-drag.js.
  const groupBaseline = useRef(null);
  const onGroupVolume = (newAvg, phase) => {
    if (memberObjs.length === 0) return;
    if (phase === 'start' || groupBaseline.current == null) {
      groupBaseline.current = {
        members: memberObjs.map((m) => ({ entity: m.entity, volume: m.volume ?? 0 })),
        avg: groupAvg,
      };
    }
    const { members, avg } = groupBaseline.current;
    groupTargets(members, avg, newAvg).forEach((t) => {
      sonosSpeakerCommand?.(t.entity, 'volume', t.volume);
    });
    if (phase === 'end') groupBaseline.current = null;
  };

  // Group footer: "Playing in <Room>" — multi-zone summary deferred to PR 5
  // (when groupMembers will be richer than today's mock seed).
  const groupCount = sonos.groupMembers?.length || 0;
  const groupLabel = groupCount > 1
    ? `Playing in ${room.name} + ${groupCount - 1} more`
    : `Playing in ${room.name}`;

  // The hero sits on a card, like every other takeover's lead surface.
  return (
    <div style={{
      display: 'flex',
      flexDirection: isPhone ? 'column' : 'row',
      gap: isPhone ? 18 : 28,
      alignItems: isPhone ? 'center' : 'flex-start',
      padding: isPhone ? '22px 20px' : '28px 28px',
      background: T.card, borderRadius: RADIUS.card, boxShadow: T.cardShadow,
    }}>
      {/* Cover — four paths.
          1. cover URL present (Spotify, radio favourite with art latched):
             render the image.
          2. Sonos is sourcing line-in (no media art will ever arrive):
             render SourceCover with the right glyph for the source
             (vinyl for Office Turntable; generic line-in jack for the
             other five line-in capable units).
          3. PR 7 — metadata-less radio: no cover, no duration (radio
             streams without latched artwork), and not a known line-in
             source. Show the radio glyph rather than the procedural
             gradient — "this is a radio stream" reads better than
             "this is generic content with a colourful background".
             Pairs with the Scrubber primitive, which already hides
             itself when duration is null.
          4. Fallback: procedural CoverArt gradient seeded from track. */}
      <div style={{
        width: coverSize, height: coverSize, borderRadius: RADIUS.control,
        overflow: 'hidden', flexShrink: 0,
        background: T.cardSoft,
      }}>
        {cover
          ? <img src={cover} alt="" width={coverSize} height={coverSize}
                 style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
          : sourceCoverKindFor(room)
            ? <SourceCover T={T} kind={sourceCoverKindFor(room)} size={coverSize} radius={RADIUS.control}/>
            : (sonos?.playing && !sonos?.mediaDuration)
              ? <SourceCover T={T} kind="radio" size={coverSize} radius={RADIUS.control}/>
              : <CoverArt seed={sonos.track || room.id} size={coverSize} radius={RADIUS.control} label={false}/>}
      </div>

      {/* Meta column */}
      <div style={{
        flex: 1, minWidth: 0, width: '100%',
        display: 'flex', flexDirection: 'column', gap: 14,
        textAlign: isPhone ? 'center' : 'left',
        alignItems: isPhone ? 'center' : 'stretch',
      }}>
        <div>
          <ALabel T={T}>Now playing</ALabel>
          <div style={{
            fontFamily: A_FONT.serif, fontSize: fs(T, isPhone ? 28 : 34),
            color: T.ink, lineHeight: 1.1, fontWeight: 500,
            letterSpacing: '-.01em', marginTop: 6,
          }}>
            {title || 'Playing'}
          </div>
          {artist && (
            <div style={{
              fontFamily: A_FONT.sans, fontSize: fs(T, 16), fontWeight: 500, color: T.ink, marginTop: 6,
            }}>
              {artist}
            </div>
          )}
          <div style={{
            fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.inkSoft,
            marginTop: 6,
          }}>
            {groupLabel}
          </div>
        </div>

        {/* Scrubber — between meta block + transport per design HANDOFF.
            Hides itself when there's no duration (radio streams). Seek
            on release via sonosCommand('seek', seconds), routed through
            usePanelRooms → media_seek. */}
        <Scrubber T={T}
          position={sonos?.mediaPosition}
          duration={sonos?.mediaDuration}
          updatedAt={sonos?.mediaPositionUpdatedAt}
          playing={!!sonos?.playing}
          onSeek={(sec) => sonosCommand?.(room.id, 'seek', sec)}/>

        {/* Transport — PR 4a primitive replacing the inline trio. */}
        <Transport T={T} viewport={viewport}
          isPlaying={!!sonos.playing}
          shuffle={!!sonos.shuffle}
          repeat={sonos.repeat || 'off'}
          onShuffle={() => sonosCommand?.(room.id, 'shuffle', !sonos.shuffle)}
          onPrev={() => sonosCommand?.(room.id, 'prev')}
          onPlayPause={() => sonosCommand?.(room.id, 'play_pause')}
          onNext={() => sonosCommand?.(room.id, 'next')}
          onRepeat={() => {
            const next = sonos.repeat === 'off' ? 'all'
                       : sonos.repeat === 'all' ? 'one' : 'off';
            sonosCommand?.(room.id, 'repeat', next);
          }}/>

        {/* Primary VolumeBar — amp-primary for Pavilion/Rumpus, Sonos-
            primary elsewhere (see primaryEntityFor). When the room is
            in a Sonos group, hide it across the board — Group Volume
            below is the single top-level control, per-member loudness
            (including the amp slider for Pavilion/Rumpus members)
            lives one click away in Group Balance. */}
        {primaryEntity && !inGroup && (
          <VolumeBar T={T} kind="primary"
            label={`Volume · ${primaryLabel}`}
            level={volume}
            onChange={(v) => sonosSpeakerCommand?.(primaryEntity, 'volume', v)}/>
        )}

        {/* Group-volume control — only when the focused room is in a
            multi-member Sonos group. Slider operates as a delta on the
            group average so individual member balance is preserved.
            TuneBalance disclosure directly below keeps the per-member
            fine-tune affordance next to the bulk control it refines —
            same conceptual surface. */}
        {inGroup && (
          <VolumeBar T={T} kind="primary"
            label={`Group volume · ${memberObjs.length} speakers`}
            level={groupAvg}
            onChange={onGroupVolume}/>
        )}

        {/* Disclosure row — Tune balance (group only), Sound (tone, when
            this room has a reachable EQ owner) and Group. The pills live
            here rather than inside each section so they read as one row
            of choices instead of two stacked strips. */}
        {showTuneBalance && (inGroup || hasEq || onOpenGroup || hasLyrics) && (
          <div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {inGroup && (
                <ActionPill T={T} icon={<DisclosureChevron open={disclosure === 'balance'}/>}
                  label={`Tune balance (${memberObjs.length})`}
                  active={disclosure === 'balance'}
                  onClick={() => setDisclosure((d) => (d === 'balance' ? null : 'balance'))}/>
              )}
              {hasEq && (
                <ActionPill T={T} icon={<DisclosureChevron open={disclosure === 'eq'}/>}
                  label={room.eq.owner ? `Sound · ${room.eq.owner}` : 'Sound'}
                  active={disclosure === 'eq'}
                  onClick={() => setDisclosure((d) => (d === 'eq' ? null : 'eq'))}/>
              )}
              {hasLyrics && (
                <ActionPill T={T} icon={<DisclosureChevron open={disclosure === 'lyrics'}/>}
                  label="Lyrics"
                  active={disclosure === 'lyrics'}
                  onClick={() => setDisclosure((d) => (d === 'lyrics' ? null : 'lyrics'))}/>
              )}
              {onOpenGroup && <GroupPill T={T} onClick={onOpenGroup}/>}
            </div>
            {disclosure === 'lyrics' && hasLyrics && (
              <LyricsPanel T={T} title={title} artist={artist}
                duration={sonos?.mediaDuration} position={sonos?.mediaPosition}
                updatedAt={sonos?.mediaPositionUpdatedAt} playing={!!sonos?.playing}/>
            )}
            {disclosure === 'balance' && inGroup && (
              <TuneBalance T={T} room={room} speakers={speakers}
                sonosSpeakerCommand={sonosSpeakerCommand}
                mediaPlayer={mediaPlayer}/>
            )}
            {disclosure === 'eq' && hasEq && (
              <EqRows T={T} room={room} eqCommand={eqCommand}/>
            )}
          </div>
        )}

        {/* Pool-extension secondary — only when active. Visually
            bracketed under primary via the extension kind. */}
        {poolLive && (
          <VolumeBar T={T} kind="extension"
            label="Pool"
            level={poolLive.volume ?? 0}
            onChange={(v) => sonosSpeakerCommand?.('media_player.drx_5_zone_3', 'volume', v)}/>
        )}
      </div>
    </div>
  );
}


// LyricsPanel — synced lyrics under the hero (LRCLIB; see music/lyrics.js).
// The sung line is ink and medium weight, the rest muted, and the box keeps
// the sung line in view by scrolling itself (never the page). Position is
// HA's media_position plus the time since media_position_updated_at, ticked
// every 250 ms while playing, the same arithmetic as the Scrubber.
export function LyricsPanel({ T, title, artist, duration, position, updatedAt, playing }) {
  const [state, setState] = useState({ status: 'loading' });
  const [attempt, setAttempt] = useState(0); // bumped by Try again
  const [now, setNow] = useState(Date.now());
  const boxRef = useRef(null);
  const lineRefs = useRef([]);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fetchLyrics({ title, artist, durationSec: duration })
      .then((v) => { if (!cancelled) setState(v); })
      .catch((e) => {
        console.warn('[music/lyrics] lookup failed', { title, artist, error: e?.name, message: e?.message });
        if (!cancelled) setState({ status: 'error', message: `${e?.name || 'Error'}: ${e?.message || 'unknown'}` });
      });
    return () => { cancelled = true; };
  }, [title, artist, duration, attempt]);

  useEffect(() => {
    if (!playing || state.status !== 'synced') return undefined;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [playing, state.status]);

  let sec = position || 0;
  if (playing && updatedAt) {
    const at = new Date(updatedAt).getTime();
    if (!Number.isNaN(at)) sec += Math.max(0, (now - at) / 1000);
  }
  const lines = state.status === 'synced' ? state.lines : [];
  const idx = lineAt(lines, sec);

  useEffect(() => {
    const box = boxRef.current;
    const el = lineRefs.current[idx];
    if (!box || !el) return;
    const target = el.offsetTop - box.clientHeight / 2 + el.clientHeight / 2;
    box.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  }, [idx]);

  const note = (text) => (
    <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.inkSoft, padding: '14px 4px', lineHeight: 1.5 }}>{text}</div>
  );
  if (state.status === 'loading') {
    return <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 4px', fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.inkSoft }}><BusyDot T={T} size={14}/>Finding lyrics…</div>;
  }
  // The reason is shown, small, because the fix depends on it: "LRCLIB 503"
  // is the service shedding load, Safari's "Load failed" / a TypeError is
  // the request never getting there (2026-10-31: every try failed on Ben's
  // device while the same request from the panel origin in Chromium worked).
  if (state.status === 'error') {
    return (
      <div style={{ padding: '14px 4px', fontFamily: A_FONT.sans, lineHeight: 1.5 }}>
        <div style={{ fontSize: fs(T, 14), color: T.inkSoft }}>Couldn’t reach the lyrics service just now. Try again in a moment.</div>
        <div style={{ fontSize: fs(T, 12), color: T.mute, marginTop: 4 }}>{state.message}</div>
        <button onClick={() => setAttempt((n) => n + 1)} style={{
          marginTop: 10, height: 40, padding: '0 16px', border: 'none', borderRadius: RADIUS.control,
          background: T.cardSoft, color: T.ink, cursor: 'pointer',
          fontFamily: A_FONT.sans, fontSize: fs(T, 14), fontWeight: 500,
        }}>Try again</button>
      </div>
    );
  }
  if (state.status === 'instrumental') return note('Instrumental: no lyrics.');
  if (state.status === 'none') return note(`No lyrics found for “${title}”.`);

  const box = {
    position: 'relative', maxHeight: 320, overflowY: 'auto', marginTop: 12,
    padding: '8px 4px', borderRadius: RADIUS.control, background: T.cardSoft,
    WebkitOverflowScrolling: 'touch',
  };
  if (state.status === 'plain') {
    return (
      <div style={box}>
        <div style={{ whiteSpace: 'pre-wrap', fontFamily: A_FONT.sans, fontSize: fs(T, 16), color: T.ink, lineHeight: 1.6, padding: '4px 12px' }}>{state.plain}</div>
        <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 12), color: T.mute, padding: '8px 12px 4px' }}>Not synced to the music.</div>
      </div>
    );
  }
  return (
    <div ref={boxRef} style={box}>
      {lines.map((l, i) => (
        <div key={i} ref={(el) => { lineRefs.current[i] = el; }} style={{
          padding: '6px 12px', minHeight: 12,
          fontFamily: A_FONT.sans, fontSize: fs(T, 18),
          fontWeight: i === idx ? 600 : 400,
          color: i === idx ? T.ink : T.mute,
          lineHeight: 1.4, transition: 'color .2s',
        }}>{l.text || '♪'}</div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// MusicShell — full-takeover surface.
//
// Opens over the top of HomeView / RoomView at z=60 (Drawer is z=50;
// MusicShell wins). ESC + back-chevron close. No backdrop card —
// fills the viewport with T.paper.
// ─────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────
// TuneBalance — per-member volume rows, used in S4/S5 when the focused
// room's Sonos is in a multi-member group. Content only: the pill that
// opens it, and the open/closed state, live in NowPlayingHero so that
// Tune balance, Sound and Group render as one row of choices.
// Coordinator row has accent eyebrow + no drop affordance.
// ─────────────────────────────────────────────────────────────────────

// Shared chrome for the Tune Balance + Group pills below the hero.
// The 2026-10 quiet button: soft fill, no outline, sans label. Active
// state (a disclosure that is open) takes the selected fill, the same
// treatment as any individually toggled chip.
function ActionPill({ T, icon, label, active = false, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      height: 48, padding: '0 18px', borderRadius: RADIUS.control,
      border: 'none',
      background: active ? T.selected : T.cardSoft,
      color: active ? T.selectedInk : T.ink, cursor: 'pointer',
      fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
      whiteSpace: 'nowrap',
      transition: 'background .15s, color .15s',
    }}>
      {icon}
      {label}
    </button>
  );
}

const GroupIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="10" r="3.5"/>
    <circle cx="16" cy="13" r="3.5"/>
    <path d="M3 20c.8-2.5 3-4 6-4"/>
    <path d="M10 21c.8-2.2 3-3.5 6-3.5s5.2 1.3 6 3.5"/>
  </svg>
);

function GroupPill({ T, onClick }) {
  return <ActionPill T={T} icon={GroupIcon} label="Group" onClick={onClick}/>;
}

function TuneBalance({ T, room, speakers, sonosSpeakerCommand, mediaPlayer }) {
  const groupMembers = room?.sonos?.groupMembers || [];
  if (groupMembers.length < 2) return null;

  // Map group_members entity_ids → speaker objects (for name + volume).
  // Coordinator convention: first entity in the group_members array.
  const coordEntity = groupMembers[0];
  const memberObjs = groupMembers
    .map((eid) => (speakers || []).find((sp) => sp.entity === eid))
    .filter(Boolean);
  if (memberObjs.length === 0) return null;

  // Pavilion + Rumpus fronted by an external amp — their Sonos digital
  // level is pinned at 80 by pavilion_listen / rumpus_listen, so the
  // member row's loudness control should target the amp, not the
  // Sonos. Other Sonos rooms (Master Bed, Office, Pool) keep Sonos as
  // the per-member control.
  const AMP_PROXY = {
    'media_player.pavilion': 'media_player.drx_3_1',
    'media_player.rumpus':   'media_player.drx_5',
  };

  return (
    <div style={{ marginTop: 12 }}>
      {memberObjs.map((m) => {
        const ampEntity = AMP_PROXY[m.entity];
        const controlEntity = ampEntity || m.entity;
        const liveVolume = ampEntity
          ? (mediaPlayer?.(ampEntity)?.volume ?? 0)
          : m.volume;
        return (
          <MemberRow key={m.entity} T={T}
            name={m.name}
            isCoordinator={m.entity === coordEntity}
            volume={liveVolume}
            onVolumeChange={(v) => sonosSpeakerCommand?.(controlEntity, 'volume', v)}
            onDrop={() => sonosSpeakerCommand?.(m.entity, 'unjoin')}/>
        );
      })}
    </div>
  );
}

// Chevron that rotates when its disclosure is open. Shared by the Tune
// balance and Sound pills so both read as the same kind of affordance.
function DisclosureChevron({ open }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
         style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .22s ease' }}>
      <path d="M6 9l6 6 6-6"/>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────
// EqRows — bass / treble for the room's EQ owner. Content only; the
// Sound pill lives in NowPlayingHero.
//
// The owner is resolved in hooks/use-panel-rooms.jsx (overlayEq) and is
// not always the device that owns volume: Pavilion and Rumpus own tone
// on their Integra amp, because their Sonos is a line-level source
// feeding the amp rather than the amplifier itself. Writes go through
// eqCommand, which targets a `number.*` for Sonos owners and an
// `input_number.*` for amp owners, where ha-av's apply automation
// pushes the value on over eISCP.
//
// Ranges come from the entity, so the step differs by owner (Sonos tone
// is 1 dB, the Integra amps are 2 dB) — that is the hardware, not a
// bug, and AudioSliderRow already reads min/max/step per control.
// ─────────────────────────────────────────────────────────────────────

function EqRows({ T, room, eqCommand }) {
  const eq = room?.eq;
  if (!eq) return null;
  const rows = [
    { key: 'bass',   label: 'Bass',   c: eq.bass },
    { key: 'treble', label: 'Treble', c: eq.treble },
  ].filter((row) => row.c);
  if (rows.length === 0) return null;

  return (
    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map((row) => {
        const c = row.c;
        const fmt = (v) => `${v > 0 ? '+' : ''}${v}${c.unit ? ' ' + c.unit : ''}`;
        return (
          <div key={row.key}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <span style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 15), color: T.ink, fontWeight: 500 }}>
                {row.label}
              </span>
              <span style={{
                fontFamily: A_FONT.sans, fontSize: fs(T, 14), fontWeight: 500,
                color: c.value === 0 ? T.mute : T.ink, fontVariantNumeric: 'lining-nums tabular-nums',
              }}>
                {fmt(c.value)}
              </span>
            </div>
            <AudioSliderRow T={T} c={c} onChange={(v) => eqCommand?.(room.id, row.key, v)}/>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DestinationsRail — chip rail + locked-chip explainer. Wraps the
// existing destinations data (room.destinations from usePanelRooms)
// in the §11.10 lock treatment.
// ─────────────────────────────────────────────────────────────────────

function DestinationsRail({ T, room, destinationsCommand }) {
  if (!room?.destinations || room.destinations.length === 0) return null;
  // Pool isn't a valid music destination — its underlying path
  // (DRX-5 zone 3 → SR2-125 analog input) only routes amp audio
  // and only when the matching room's TV/AV stack is on. For Sonos
  // music the correct way to reach Pool is grouping Pool's Sonos
  // into the current group via the Group editor. Filter it out here
  // so the chip rail in the music card only surfaces destinations
  // that actually do something for music.
  const destinations = room.destinations.filter((d) => d.id !== 'pool');
  if (destinations.length === 0) return null;
  const lockedActive = destinations.find((d) => d.locked && d.active);
  // Plain-English explainer message per §11.10. Parametrised so the same
  // copy works for Pavilion's Kitchen and Rumpus's Rumpus-zone lock.
  const message = lockedActive
    ? `${lockedActive.name} follows TV. While the ${room.name} TV is on, ${lockedActive.name} audio is hardware-linked and can't be dropped without also turning off the TV.`
    : null;

  return (
    <Card T={T} eyebrow="Playing in">
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {destinations.map((d) => (
          <DestChip key={d.id} T={T}
            name={d.name}
            active={d.active}
            locked={d.locked}
            lockReason={d.lockReason}
            onClick={() => destinationsCommand?.(room.id, d.id, !d.active)}/>
        ))}
      </div>
      {message && <DestExplainer T={T} message={message}/>}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// S6Editor — group editor sub-view. Checkbox-style list of all 14
// Sonos units; pre-checks the focused room's current group members.
// Save → diff (toJoin / toUnjoin) → media_player.join / unjoin
// applied via onSave callback. Coordinator stays put; if user
// un-checks the coordinator, onSave keeps it in (a group needs a
// leader). Changing coordinator deferred to a polish PR.
// ─────────────────────────────────────────────────────────────────────

function S6Editor({ T, room, speakers, onSave, onCancel }) {
  // Build a stable "all Sonos" list — speakers prop comes from
  // overlaySpeakers in usePanelRooms, already 14 units.
  const allSpeakers = speakers || [];
  const groupMembers = room?.sonos?.groupMembers || [];
  const coordinator = groupMembers[0] || /* solo room → its own Sonos */ (
    ({
      office: 'media_player.office',
      pavilion: 'media_player.pavilion',
      rumpus: 'media_player.rumpus',
      master_bed: 'media_player.master_bed',
      master_ensuite: 'media_player.ensuite',
      pool: 'media_player.pool',
    })[room?.id] || null
  );

  // Selection state: start with current group (or just the room itself).
  const initialSelection = new Set(
    groupMembers.length > 0 ? groupMembers : (coordinator ? [coordinator] : [])
  );
  const [selection, setSelection] = useState(initialSelection);
  // Save is a multi-second async chain (amp-listen guard, per-member
  // unjoin, 400ms settle, join) with no visible change until it lands,
  // so the button holds busy state until onSave's promise resolves —
  // the S4 view flip unmounts this editor on success.
  const [saving, setSaving] = useState(false);

  const toggle = (entity) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(entity)) next.delete(entity);
      else next.add(entity);
      return next;
    });
  };

  const coordName = (allSpeakers.find((s) => s.entity === coordinator)?.name) || coordinator || '—';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{
        padding: '16px 20px',
        background: T.cardSoft, borderRadius: RADIUS.control,
      }}>
        <ALabel T={T}>
          {selection.size} selected · {coordName} coordinator
        </ALabel>
        <div style={{
          fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.inkSoft,
          marginTop: 6, lineHeight: 1.45,
        }}>
          Tick speakers to add them, untick to drop them — including the
          coordinator. If you drop the coordinator and others remain,
          this room takes over as the new leader (audio briefly pauses
          during the handover).
        </div>
      </div>

      {/* Speaker list on a card. Each row is the whole tap target (the
          label wraps the checkbox); the native box is visually hidden
          and a drawn mark stands in for it, chosen = selected fill. */}
      <div style={{
        background: T.card, borderRadius: RADIUS.card, boxShadow: T.cardShadow,
        padding: '6px 20px',
      }}>
        {allSpeakers.map((sp, i) => {
          const checked = selection.has(sp.entity);
          const isCoord = sp.entity === coordinator;
          return (
            <label key={sp.entity} style={{
              display: 'flex', alignItems: 'center', gap: 14,
              minHeight: 60, padding: '8px 0', cursor: 'pointer',
              position: 'relative',
              borderBottom: i < allSpeakers.length - 1 ? `1px solid ${T.hair}` : 'none',
            }}>
              <input type="checkbox" checked={checked}
                onChange={() => toggle(sp.entity)}
                style={{
                  position: 'absolute', opacity: 0, width: 1, height: 1,
                  margin: 0, pointerEvents: 'none',
                }}/>
              <span aria-hidden style={{
                width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                background: checked ? T.selected : T.cardSoft,
                color: T.selectedInk,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background .15s',
              }}>
                {checked && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12.5l4.5 4.5L19 7.5"/>
                  </svg>
                )}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
                  color: T.ink, lineHeight: 1.25,
                }}>{sp.name}</div>
                {isCoord && (
                  <div style={{
                    fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft,
                    marginTop: 2,
                  }}>
                    Coordinator
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={saving ? undefined : onCancel} disabled={saving}
          style={{
            height: 52, padding: '0 24px', borderRadius: RADIUS.control,
            border: 'none', background: T.cardSoft,
            color: T.ink, cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.45 : 1,
            fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
          }}>
          Cancel
        </button>
        {/* In flight, Save lifts out as a key with the busy dot rather
            than staying a black primary that looks unpressed. */}
        <button disabled={saving} aria-busy={saving || undefined}
          onClick={async () => {
            if (saving) return;
            setSaving(true);
            try { await onSave([...selection], coordinator); }
            finally { setSaving(false); }
          }}
          style={{
            height: 52, padding: '0 28px', borderRadius: RADIUS.control,
            border: 'none',
            background: saving ? T.keyFill : T.primary,
            boxShadow: saving ? T.keyShadow : 'none',
            color: saving ? T.ink : T.primaryInk,
            cursor: saving ? 'default' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 8,
            fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
          }}>
          {saving && <BusyDot T={T} size={14}/>}
          {saving ? 'Regrouping…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// QueueRowImage — small (44px) thumbnail for a single queue row.
// HA's sonos.get_queue exposes the cover URL across several possible
// field names depending on integration version; pick the first
// truthy. http→https upgrade in case Sonos returns its own (HTTP-only)
// URL on an HTTPS-served panel. HA-proxy paths route through
// useImageBlobUrl for auth; absolute URLs render direct. Falls back
// to the procedural CoverArt gradient when no URL or the image
// errors out.
function QueueRowImage({ T, item, enrichedUrl }) {
  const raw = enrichedUrl || item.media_image_url || item.image_url || item.album_art_uri || item.thumbnail || null;
  const httpsUrl = (raw && raw.startsWith('http://')) ? 'https://' + raw.slice(7) : raw;
  const isHaProxy = !!httpsUrl && httpsUrl.startsWith('/');
  const blobUrl = useImageBlobUrl(isHaProxy ? httpsUrl : null);
  const displayUrl = isHaProxy ? blobUrl : httpsUrl;
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { setFailed(false); }, [displayUrl]);
  const show = !!displayUrl && !failed;
  return (
    <div style={{
      width: 44, height: 44, borderRadius: 8, overflow: 'hidden',
      background: T.cardSoft, flexShrink: 0,
    }}>
      {show
        ? <img src={displayUrl} alt="" width={44} height={44}
               onError={() => setFailed(true)}
               style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}/>
        : <CoverArt seed={item.media_content_id || item.media_title} size={44} radius={8} label={false}/>}
    </div>
  );
}

// NowPlayingQueue — the "Up next" section in room-focused S4. Fetches
// the Sonos queue once per room change via getSonosQueue (which calls
// sonos.get_queue under the hood). Renders the upcoming tracks after
// the currently-playing item. Hidden when queue is empty (radio,
// single-track playback, or nothing queued).
// ─────────────────────────────────────────────────────────────────────

// spotifyTrackIdFrom (Sonos content id → Spotify track id) now lives in
// save-playlist.js, shared with "Save this queue".

// Module-scope cache: spotify track ID → cover URL (or null when
// Spotify returned no usable image). Lives for the panel session, so
// repeated queue refreshes / MusicShell reopens never re-fetch the
// same track. Critical for staying under Spotify's per-app rate limit
// — without it we were burning through /v1/tracks calls on every
// track change. Cache misses only fetch the visible-rows slice (≤6
// IDs at a time) to further reduce pressure.
const queueArtCache = new Map();

// Extract the Apple library-song id from a sonos.get_queue content_id.
// Shape: `x-sonos-http:librarytrack%3a<i.id>.mp4?sid=204&...` (URL-encoded).
// Returns null for non-Apple queue items.
function appleTrackIdFrom(contentId) {
  if (!contentId) return null;
  let decoded;
  try { decoded = decodeURIComponent(contentId); } catch { decoded = contentId; }
  const m = decoded.match(/librarytrack:(i\.[A-Za-z0-9]+)/);
  return m ? m[1] : null;
}
// Apple library-song id → cover URL. Same session-cache role as queueArtCache.
const appleArtCache = new Map();

export function NowPlayingQueue({ T, roomId, roomName, roomEntity, hass, getSonosQueue, currentContentId, currentTitle, getToken }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // id → cover URL, enriched from Spotify Web API. sonos.get_queue
  // returns only title/artist/album/content_id — no art. For Spotify-
  // backed queue items we batch-fetch /v1/tracks?ids= and stash the
  // result here so QueueRowImage can render covers instead of
  // procedural gradients.
  const [artById, setArtById] = useState({});
  // Bumped after "Keep it going" adds tracks, so the list re-reads.
  const [reload, setReload] = useState(0);

  // getSonosQueue is a useCallback([hass]) inside usePanelRooms, so its
  // identity churns on every HA state event. Capturing it via a ref
  // keeps the queue-fetch effect from re-firing constantly (same
  // flicker pattern as use-spotify-token / use-sonos-favourites in
  // PR 2). The effect refetches only when the focused room or current
  // track actually changes — both genuine signals that the queue may
  // differ.
  const queueRef = React.useRef(getSonosQueue);
  useEffect(() => { queueRef.current = getSonosQueue; }, [getSonosQueue]);

  useEffect(() => {
    if (!roomId) { setItems([]); setLoaded(false); return; }
    const fetcher = queueRef.current;
    if (!fetcher) { setItems([]); setLoaded(false); return; }
    let cancelled = false;
    setLoaded(false);
    fetcher(roomId).then((arr) => {
      if (cancelled) return;
      setItems(Array.isArray(arr) ? arr : []);
      setLoaded(true);
    });
    return () => { cancelled = true; };
  }, [roomId, currentContentId, reload]);

  // Spotify art enrichment — sonos.get_queue returns no cover URLs.
  // Only enriches the visible-rows slice (≤6 IDs) to stay under
  // Spotify's per-app rate limit (heavy enrichment was 429-ing the
  // whole token). Module-scope queueArtCache means we only fetch
  // each track ID once per session even when the queue refreshes
  // around it. Runs in its own effect so it re-fires when getToken
  // becomes available after queue fetch (token hook can resolve
  // later than queue on first MusicShell open).
  useEffect(() => {
    if (items.length === 0 || !getToken) return;
    // Mirror the render-time slicing so we only enrich what's shown.
    const currentIdx = (currentContentId || currentTitle)
      ? items.findIndex((i) =>
          (currentContentId && i.media_content_id === currentContentId)
          || (currentTitle && i.media_title === currentTitle))
      : -1;
    const upcoming = currentIdx >= 0 ? items.slice(currentIdx + 1) : items;
    const display = upcoming.slice(0, 6);

    // Hydrate from cache first; collect uncached IDs for fetch.
    const cacheHits = {};
    const toFetch = new Map();  // spotifyId → contentId
    for (const it of display) {
      const sid = spotifyTrackIdFrom(it.media_content_id);
      if (!sid) continue;
      if (queueArtCache.has(sid)) {
        const cached = queueArtCache.get(sid);
        if (cached) cacheHits[it.media_content_id] = cached;
      } else if (!toFetch.has(sid)) {
        toFetch.set(sid, it.media_content_id);
      }
    }
    if (Object.keys(cacheHits).length > 0) {
      setArtById((prev) => ({ ...prev, ...cacheHits }));
    }
    if (toFetch.size === 0) return;

    let cancelled = false;
    (async () => {
      let token;
      try { token = await getToken(); }
      catch (e) {
        console.warn('[music/queue] token fetch failed, skipping art enrichment', e);
        return;
      }
      const ids = [...toFetch.keys()];
      const map = {};
      // Singular /v1/tracks/<id> calls in parallel — the batched
      // /v1/tracks?ids=... endpoint was returning 403 for this app
      // (Spotify Dev-Mode quirk or extended rate-limit cool-down on
      // that URL pattern). Singular endpoint accepts the same token
      // and returns the same shape per item. Visible-slice cap of 6
      // keeps the per-cycle request count well below any limit.
      const results = await Promise.all(ids.map(async (id) => {
        try {
          const res = await fetch(`https://api.spotify.com/v1/tracks/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) return null;
          return await res.json();
        } catch {
          return null;
        }
      }));
      for (const t of results) {
        if (!t?.id) continue;
        const url = t.album?.images?.[0]?.url || null;
        queueArtCache.set(t.id, url);
        if (url) {
          const contentId = toFetch.get(t.id);
          if (contentId) map[contentId] = url;
        }
      }
      if (!cancelled && Object.keys(map).length > 0) {
        setArtById((prev) => ({ ...prev, ...map }));
      }
    })();
    return () => { cancelled = true; };
  }, [items, getToken, currentContentId, currentTitle]);

  // Apple art enrichment — Apple queue items carry only a library-song id in the
  // content_id (no art, no usable Sonos URL). Look covers up via the HA-gated
  // /music-api/apple-lib?songs= (which uses the stored MUT). Mirrors the Spotify
  // enrichment above; feeds the same artById sink keyed by content_id.
  useEffect(() => {
    if (items.length === 0 || !hass?.auth) return;
    const currentIdx = (currentContentId || currentTitle)
      ? items.findIndex((i) =>
          (currentContentId && i.media_content_id === currentContentId)
          || (currentTitle && i.media_title === currentTitle))
      : -1;
    const upcoming = currentIdx >= 0 ? items.slice(currentIdx + 1) : items;
    const display = upcoming.slice(0, 6);

    const cacheHits = {};
    const toFetch = new Map(); // appleId → contentId
    for (const it of display) {
      const aid = appleTrackIdFrom(it.media_content_id);
      if (!aid) continue;
      if (appleArtCache.has(aid)) {
        const cached = appleArtCache.get(aid);
        if (cached) cacheHits[it.media_content_id] = cached;
      } else if (!toFetch.has(aid)) {
        toFetch.set(aid, it.media_content_id);
      }
    }
    if (Object.keys(cacheHits).length > 0) setArtById((prev) => ({ ...prev, ...cacheHits }));
    if (toFetch.size === 0) return;

    let cancelled = false;
    (async () => {
      let token;
      try {
        const auth = hass.auth;
        if (auth.expired) await auth.refreshAccessToken();
        token = auth.accessToken;
      } catch { return; }
      const ids = [...toFetch.keys()];
      try {
        const res = await fetch(`/music-api/apple-lib?songs=${encodeURIComponent(ids.join(','))}`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const arts = data.arts || {};
        const failed = new Set(data.failed || []);
        const map = {};
        for (const aid of ids) {
          // Skip transient lookup failures and ids the endpoint didn't return:
          // leave them uncached so the next queue cycle retries. Only cache a
          // genuine result (a cover URL, or null = the song truly has no art).
          if (failed.has(aid) || !(aid in arts)) continue;
          const url = arts[aid] || null;
          appleArtCache.set(aid, url);
          if (url) { const cid = toFetch.get(aid); if (cid) map[cid] = url; }
        }
        if (!cancelled && Object.keys(map).length > 0) setArtById((prev) => ({ ...prev, ...map }));
      } catch { /* leave gradient placeholder */ }
    })();
    return () => { cancelled = true; };
  }, [items, hass, currentContentId, currentTitle]);

  if (!loaded || items.length === 0) return null;

  // Find current position in queue. media_content_id (Spotify URI)
  // is the unambiguous match — title fallback covers radio / non-
  // Spotify content where IDs may differ between the playing-state
  // attribute and the queue entry.
  const currentIdx = (currentContentId || currentTitle)
    ? items.findIndex((i) =>
        (currentContentId && i.media_content_id === currentContentId)
        || (currentTitle && i.media_title === currentTitle))
    : -1;
  const upcoming = currentIdx >= 0 ? items.slice(currentIdx + 1) : items;
  // The card stays for "Save this queue" while the queue holds two or more
  // tracks, even on the last one when there is nothing left "Up next".
  if (upcoming.length === 0 && items.length < 2) return null;

  const display = upcoming.slice(0, 6);
  const more = upcoming.length - display.length;

  // Tap-to-play handler. `sonos.play_queue` (NOT `play_from_queue` —
  // verified 2026-05-24 against HA's /api/services for the sonos
  // domain) jumps the room's playback to the given queue position.
  // `roomEntity` must be a Sonos in the group (any member works —
  // Sonos resolves to the coordinator internally). Absolute position
  // is currentIdx + 1 + i when there's a currently-playing track,
  // else just i (no current means user is browsing a paused queue).
  const onPlayQueueItem = (absPosition) => {
    if (!roomEntity || !hass?.callService) return;
    hass.callService('sonos', 'play_queue', {
      entity_id: roomEntity,
      queue_position: absPosition,
    });
  };
  const clickable = !!(roomEntity && hass?.callService);

  return (
    <Card T={T} eyebrow={upcoming.length ? 'Up next' : 'Queue'} style={{ gap: 6 }}>
      {upcoming.length > 0 && <div>
        {display.map((it, i) => {
          const absPos = currentIdx >= 0 ? currentIdx + 1 + i : i;
          return (
            <div key={i}
              onClick={clickable ? () => onPlayQueueItem(absPos) : undefined}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={clickable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onPlayQueueItem(absPos);
                }
              } : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                minHeight: 64, padding: '8px 4px', boxSizing: 'border-box',
                borderBottom: i < display.length - 1 || more > 0 ? `1px solid ${T.hair}` : 'none',
                fontFamily: A_FONT.sans,
                cursor: clickable ? 'pointer' : 'default',
                transition: 'background .12s',
              }}
              onMouseEnter={clickable ? (e) => { e.currentTarget.style.background = T.cardSoft; } : undefined}
              onMouseLeave={clickable ? (e) => { e.currentTarget.style.background = 'transparent'; } : undefined}>
              <span style={{
                fontSize: fs(T, 13), color: T.mute,
                fontVariantNumeric: 'lining-nums tabular-nums', width: 20, textAlign: 'right', flexShrink: 0,
              }}>{i + 1}</span>
              <QueueRowImage T={T} item={it} enrichedUrl={artById[it.media_content_id]}/>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500, color: T.ink,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{it.media_title || '(untitled)'}</div>
                {it.media_artist && (
                  <div style={{
                    fontSize: fs(T, 13), color: T.inkSoft, marginTop: 2,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{it.media_artist}</div>
                )}
              </div>
            </div>
          );
        })}
        {more > 0 && (
          <div style={{
            padding: '12px 0 4px',
            fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.inkSoft,
          }}>
            +{more} more in queue
          </div>
        )}
      </div>}
      <KeepGoing T={T} hass={hass} roomEntity={roomEntity}
        readQueue={() => (queueRef.current ? queueRef.current(roomId) : Promise.resolve([]))}
        onAdded={() => setReload((n) => n + 1)}/>
      {items.length >= 2 && (
        <SaveQueue T={T} hass={hass} getToken={getToken} roomName={roomName}
          queueCount={items.length}
          readQueue={() => (queueRef.current ? queueRef.current(roomId) : Promise.resolve([]))}/>
      )}
    </Card>
  );
}

// KeepGoing — "Keep it going": Claude adds ~15 tracks in the vein of the end
// of the queue, so the music doesn't stop when the queue runs out. Spotify's
// own radio / recommendations are closed to Development Mode apps (checked
// 2026-10-31), so this is the builder's generate → resolve → enqueue chain
// with the queue's last ten tracks as seeds and the whole queue excluded.
// One run per tap; the button is inert while it runs.
function KeepGoing({ T, hass, roomEntity, readQueue, onAdded }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [result, setResult] = useState('');
  const [error, setError] = useState('');

  async function run() {
    if (busy || !roomEntity) return;
    setBusy(true); setError(''); setResult('');
    try {
      const auth = hass?.auth;
      if (!auth) throw new Error('Not connected to Home Assistant.');
      if (auth.expired) await auth.refreshAccessToken();
      const headers = { 'content-type': 'application/json', authorization: `Bearer ${auth.accessToken}` };

      setStatus('Reading the queue…');
      const items = await readQueue();
      const req = keepGoingRequest(items);
      if (!req.seeds.length) throw new Error('Nothing in this queue to go on from.');

      setStatus('Choosing more like this…');
      const gRes = await fetch('/music-api/generate', {
        method: 'POST', headers,
        body: JSON.stringify({
          ...req, allow_explicit: true,
          brief: 'Carry this listening session on in the same vein, flowing on from the last few seed tracks.',
        }),
      });
      const gData = await gRes.json().catch(() => ({}));
      if (!gRes.ok) throw new Error(gData.error || `generate ${gRes.status}`);

      setStatus('Finding them on Spotify…');
      const rRes = await fetch('/music-api/resolve', {
        method: 'POST', headers,
        body: JSON.stringify({ candidates: gData.candidates || [], target_length: req.target_length, allow_explicit: true }),
      });
      const rData = await rRes.json().catch(() => ({}));
      if (!rRes.ok) throw new Error(rData.error || `resolve ${rRes.status}`);

      const queued = items.map((it) => spotifyTrackIdFrom(it?.media_content_id)).filter(Boolean);
      const add = freshTracks(rData.tracks, queued, req.exclude);
      if (!add.length) throw new Error('Nothing new turned up. Try again.');

      setStatus(`Adding ${add.length} to the queue…`);
      await createDispatch(hass).queueTracks(roomEntity, add.map((t) => ({ kind: 'track', id: t.id })));
      setResult(`Added ${add.length} tracks to the end of the queue.`);
      onAdded?.();
    } catch (e) {
      setError(`Keep it going failed: ${e.message}`);
    } finally {
      setBusy(false); setStatus('');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
      <button onClick={run} disabled={busy} style={{
        width: '100%', height: 48, border: 'none', borderRadius: RADIUS.control,
        padding: '0 14px', fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        background: busy ? T.keyFill : T.cardSoft, boxShadow: busy ? T.keyShadow : 'none',
        color: T.ink, cursor: busy ? 'default' : 'pointer',
      }}>
        {busy && <BusyDot T={T} size={14}/>}
        {busy ? status : 'Keep it going: add 15 more like this'}
      </button>
      {result && <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.ink, fontWeight: 500 }}>{result}</div>}
      {error && <div style={{ fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.attention, fontWeight: 500, lineHeight: 1.45 }}>{error}</div>}
    </div>
  );
}

// SaveQueue — "Save this queue" at the foot of the queue card: turn what the
// room is playing back into a playlist after the fact (family request
// 2026-10-31). Re-reads the queue at the moment of saving, so it saves what
// is on the speaker now, not what was on screen when the card rendered.
// Spotify: ids straight from the queue, non-Spotify rows matched through
// /music-api/resolve. Apple: {title, artist} through /music-api/apple-save.
// One save per tap; a service that has saved this name is disabled until the
// name changes, so a second tap can't make a duplicate.
function SaveQueue({ T, hass, getToken, roomName, queueCount, readQueue }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(null);        // 'spotify' | 'apple' | null
  const [status, setStatus] = useState('');
  const [results, setResults] = useState([]);    // lines of plain text
  const [error, setError] = useState('');
  const [savedTo, setSavedTo] = useState({});     // { spotify: true, apple: true }

  const begin = () => {
    setName(defaultQueueName(roomName));
    setResults([]); setError(''); setSavedTo({}); setOpen(true);
  };
  const rename = (v) => { setName(v); setSavedTo({}); };
  const finalName = () => name.trim() || defaultQueueName(roomName);

  async function toSpotify() {
    if (busy) return;
    setBusy('spotify'); setError('');
    try {
      if (typeof getToken !== 'function') throw new Error('Spotify isn’t connected on this panel.');
      setStatus('Reading the queue…');
      let { rows, skipped } = planQueue(await readQueue());
      if (!rows.length) throw new Error('Nothing in this queue can be saved.');
      const unmatched = rows.filter((r) => !r.uri).length;
      if (unmatched) {
        setStatus(`Finding ${unmatched} on Spotify…`);
        rows = await resolveMissing(hass, rows);
      }
      const uris = rows.filter((r) => r.uri).map((r) => r.uri);
      if (!uris.length) throw new Error('None of these tracks were found on Spotify.');
      setStatus('Saving to Spotify…');
      const n = finalName();
      const r = await saveToSpotify(getToken, {
        name: n, uris,
        description: `Saved from the ${roomName || 'Sonos'} queue in the HA controller.`,
      });
      const missing = rows.length - uris.length;
      setResults((xs) => [...xs, `Saved ${r.count} tracks to Spotify as “${n}”.`
        + (missing ? ` ${missing} not found on Spotify.` : '')
        + (skipped ? ` ${skipped} radio or line-in item${skipped === 1 ? '' : 's'} left out.` : '')]);
      setSavedTo((m) => ({ ...m, spotify: true }));
    } catch (e) {
      setError(e.status === 401 || e.status === 403
        ? `Spotify refused the save (${e.status}). Home Assistant's Spotify connection may need reconnecting.`
        : `Spotify save failed: ${e.message}`);
    } finally {
      setBusy(null); setStatus('');
    }
  }

  async function toApple() {
    if (busy) return;
    setBusy('apple'); setError('');
    try {
      setStatus('Reading the queue…');
      const { rows, skipped } = planQueue(await readQueue());
      const titled = rows.filter((r) => r.title);
      if (!titled.length) throw new Error('Nothing in this queue can be saved.');
      setStatus('Saving to Apple Music…');
      const n = finalName();
      const data = await saveToApple(hass, { name: n, rows: titled });
      const missed = (data.misses || []).length;
      const over = Math.max(0, titled.length - APPLE_CAP);
      setResults((xs) => [...xs, `Saved ${data.playlist?.count ?? ''} tracks to Apple Music as “${n}”.`
        + (missed ? ` ${missed} not in Apple’s catalogue.` : '')
        + (over ? ` Apple saves take the first ${APPLE_CAP}; ${over} left out.` : '')
        + (skipped ? ` ${skipped} radio or line-in item${skipped === 1 ? '' : 's'} left out.` : '')]);
      setSavedTo((m) => ({ ...m, apple: true }));
    } catch (e) {
      setError(e.code === 'apple_not_connected'
        ? 'Apple Music isn’t connected. Open the minter app on the phone to connect.'
        : e.code === 'apple_token_expired'
          ? 'Apple Music sign-in has expired. Reconnect in the minter app on the phone.'
          : e.code === 'no_matches'
            ? 'None of these tracks were on Apple Music.'
            : `Apple save failed: ${e.message}`);
    } finally {
      setBusy(null); setStatus('');
    }
  }

  const btn = (kind, done) => ({
    flex: 1, minWidth: 0, height: 48, border: 'none', borderRadius: RADIUS.control,
    padding: '0 14px', fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    background: busy && busy === kind ? T.keyFill : T.cardSoft,
    boxShadow: busy && busy === kind ? T.keyShadow : 'none',
    color: done ? T.mute : T.ink,
    cursor: busy || done ? 'default' : 'pointer',
  });
  const line = { fontFamily: A_FONT.sans, fontSize: fs(T, 14), color: T.ink, fontWeight: 500, lineHeight: 1.45 };
  const note = { fontFamily: A_FONT.sans, fontSize: fs(T, 13), color: T.mute, lineHeight: 1.5 };

  if (!open) {
    return (
      <button onClick={begin} style={{ ...btn(null, false), flex: 'none', width: '100%', marginTop: 10 }}>
        Save this queue as a playlist
      </button>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
      <input value={name} onChange={(e) => rename(e.target.value)} placeholder="Playlist name"
        aria-label="Playlist name"
        style={{
          width: '100%', height: 48, boxSizing: 'border-box', background: T.cardSoft, border: 'none',
          borderRadius: RADIUS.control, padding: '0 16px', color: T.ink, outline: 'none',
          fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
        }}/>
      <div style={{ display: 'flex', gap: 10 }}>
        <button style={btn('spotify', savedTo.spotify)} disabled={!!busy || savedTo.spotify} onClick={toSpotify}>
          {busy === 'spotify' && <BusyDot T={T} size={14}/>}
          {busy === 'spotify' ? status : savedTo.spotify ? 'Saved to Spotify' : 'Save to Spotify'}
        </button>
        <button style={btn('apple', savedTo.apple)} disabled={!!busy || savedTo.apple} onClick={toApple}>
          {busy === 'apple' && <BusyDot T={T} size={14}/>}
          {busy === 'apple' ? status : savedTo.apple ? 'Saved to Apple Music' : 'Save to Apple Music'}
        </button>
      </div>
      {results.map((r, i) => <div key={i} style={line}>{r}</div>)}
      {error && <div style={{ ...line, color: T.attention }}>{error}</div>}
      <div style={note}>
        Saves the whole queue as it stands when you tap ({queueCount} tracks now), including anything
        added since it started. Spotify makes the playlist public whatever is asked; Apple saves take
        the first {APPLE_CAP}.
        {' '}<span role="button" tabIndex={0} onClick={() => !busy && setOpen(false)}
          onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !busy) setOpen(false); }}
          style={{ color: T.inkSoft, fontWeight: 500, cursor: busy ? 'default' : 'pointer' }}>Close</span>
      </div>
    </div>
  );
}

// LineInPicker — modal overlay from S0 / S4 Sources row. Opens when
// the user taps the Line-in tile; lists every Sonos that reports a
// "Line-in" entry in its source_list attribute, queried fresh from
// hass.states on each open. Dynamic discovery is deliberate — the use
// case includes ad-hoc plug-in usage (phone, CD player, guest laptop
// into any Connect / Connect:Amp / Port jack) where a maintained
// allowlist would lag the physical world. See BRIEF-music-card.md §5
// and feedback memory [[feedback-dynamic-discovery-for-hardware-state]].
//
// Rendered at z=70 over MusicShell (z=60). Backdrop-click and ESC both
// cancel; tapping a row calls onPick(speaker) and the parent transitions
// either to S3 (content-first) or straight to dispatch (room-first per
// §11.16 applied to line-in source selection).
function LineInPicker({ T, hass, speakers, onPick, onCancel }) {
  const items = (speakers || [])
    .map((sp) => {
      const sources = hass?.states?.[sp.entity]?.attributes?.source_list || [];
      const hasLineIn = sources.some((s) => /line[-_ ]?in/i.test(s));
      return hasLineIn ? sp : null;
    })
    .filter(Boolean);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div onClick={onCancel} style={{
      // Scrim spans the full viewport (dark fill is fine under iOS
      // chrome) but the padding is safe-area-aware so the centered
      // modal inside doesn't get clipped by the notch / home indicator.
      position: 'fixed', inset: 0, zIndex: 70,
      background: T.scrim,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 'calc(20px + env(safe-area-inset-top)) calc(20px + env(safe-area-inset-right)) calc(20px + env(safe-area-inset-bottom)) calc(20px + env(safe-area-inset-left))',
      fontFamily: A_FONT.sans,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: T.card, color: T.ink,
        border: 'none', borderRadius: RADIUS.card,
        padding: '24px 24px 20px', maxWidth: 440, width: '100%',
        boxSizing: 'border-box',
        maxHeight: '80vh', overflowY: 'auto',
        boxShadow: T.shadow,
      }}>
        <ALabel T={T} style={{ marginBottom: 6 }}>Line-in source</ALabel>
        <div style={{
          fontFamily: A_FONT.serif, fontWeight: 500, lineHeight: 1.1,
          fontSize: fs(T, 28), color: T.ink, marginBottom: 18,
        }}>
          Pick a speaker
        </div>

        {items.length === 0 && (
          <div style={{
            color: T.inkSoft, fontSize: fs(T, 14),
            padding: '4px 0 14px', lineHeight: 1.5,
          }}>
            No speakers report a line-in source right now. Sonos units
            expose <i>Line-in</i> via their <code>source_list</code>
            attribute — check the Sonos app if a unit you expect is
            missing.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((sp) => (
            <button key={sp.entity}
              onClick={() => onPick?.(sp)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                minHeight: 52, padding: '0 16px', borderRadius: RADIUS.control,
                border: 'none', background: T.cardSoft,
                color: T.ink, cursor: 'pointer', textAlign: 'left',
                fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
                transition: 'background .12s, box-shadow .12s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = T.keyFill;
                e.currentTarget.style.boxShadow = T.keyShadow;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = T.cardSoft;
                e.currentTarget.style.boxShadow = 'none';
              }}>
              <span style={{
                width: 26, height: 26, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                color: T.inkSoft, flexShrink: 0,
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="1.4"
                     strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>{sp.name}</span>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onCancel} style={{
            height: 48, padding: '0 22px', borderRadius: RADIUS.control,
            border: 'none', background: T.cardSoft,
            color: T.ink, cursor: 'pointer',
            fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
          }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function MusicShell({
  T, open, onClose, rooms, focusRoomId, viewport, hass, speakers,
  sonosCommand, sonosSpeakerCommand, mediaPlayer, poolExtension,
  destinationsCommand, getSonosQueue, onOpenBuilder, eqCommand,
}) {
  // Spotify token bridge — see ../hooks/use-spotify-token.jsx for the
  // identity-stability dance. Pass null when shell is closed so the
  // hook stays inert.
  const spotify = useSpotifyToken(open ? hass : null);

  // View state machine — S0 (home) | S1 (search) | S2 (detail) |
  // S3 (target picker) | S4 (focused now-playing takeover).
  // searchQuery persists across S0 ↔ S1 toggles within a single shell
  // open; resets when the shell closes + reopens.
  const [view, setView] = useState({ mode: 'S0' });
  const [searchQuery, setSearchQuery] = useState('');
  // After-dispatch hero fallback art. HA's Sonos integration doesn't
  // push a media_image for radio favourites (they stream without
  // metadata art), so without this the hero shows the procedural
  // gradient. Latch the dispatched tile's thumbnail; HA's mediaImage
  // wins when it exists, so Spotify dispatches still show the real
  // album cover.
  const [dispatchedArt, setDispatchedArt] = useState(null);
  // Line-in picker visibility — opened by SourceTile tap in S0 / S4.
  const [lineInPickerOpen, setLineInPickerOpen] = useState(false);

  // Reset on shell open. focusRoomId-set entry (room tile, NowPlayingStrip
  // tap) lands at S4 — the focused now-playing takeover — per §11.16's
  // room-first rule. focusRoomId-null entry (drawer Music link) lands
  // at S0 with carousels, the browse-first surface.
  useEffect(() => {
    if (open) {
      setView({ mode: focusRoomId ? 'S4' : 'S0' });
      setSearchQuery('');
    }
  }, [open]);

  // ESC handler — TakeoverShell wires ESC to `onBack`, and we pass
  // `goBack` as onBack below, so pressing ESC pops one view-layer
  // (S6 → S4, S2/S3 → S0, etc) before finally closing on S0/drawer-first
  // S4. No standalone keydown listener needed here.

  // HA Frontend keyboard-shortcut blocker. HA listens at document level
  // (capture phase) for shortcuts like 'a'=Assist, 'c'=command palette,
  // 'e'=edit, 'm'=menu — so typing "Olivia Dean" in our search input
  // pops the Assist dialog on the 'a'. Capture phase fires window →
  // document → target, so a listener attached on *window* in capture
  // phase fires before HA's document-level listener. We check the
  // currently-focused element via data-music-search="1" and swallow
  // the event with stopImmediatePropagation. Escape is allowed through
  // so the ESC handler above still works.
  useEffect(() => {
    if (!open) return;
    const onK = (e) => {
      if (e.key === 'Escape') return;
      const active = document.activeElement;
      if (active && active.dataset && active.dataset.musicSearch === '1') {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener('keydown', onK, true);
    return () => window.removeEventListener('keydown', onK, true);
  }, [open]);

  // Dispatch chain (PR 3). Must be declared BEFORE the early-return —
  // hook-count must match every render or React throws "Rendered more
  // hooks than during the previous render" and the shell never mounts.
  const dispatch = React.useMemo(() => createDispatch(hass), [hass]);
  // Latest hass for async waits (a closure's hass is frozen at call time).
  const hassRef = useRef(hass);
  hassRef.current = hass;

  if (!open) return null;

  const focusRoom = focusRoomId
    ? rooms.find(r => r.id === focusRoomId)
    : findPlayingRoom(rooms);

  // bodyless TakeoverShell means we own our own inner padding; the
  // shell paints paper edge-to-edge including the home-indicator
  // strip, so add env(safe-area-inset-bottom) to keep content above
  // it on notched iPhones. Sides are handled at the shell level too,
  // but the music body is column-centered so left/right env() doesn't
  // add up here — leave it on the shell.
  const padding = viewport === 'phone' ? '14px 18px calc(28px + env(safe-area-inset-bottom))'
                : viewport === 'tablet' ? '20px 26px calc(28px + env(safe-area-inset-bottom))'
                : '22px 36px calc(36px + env(safe-area-inset-bottom))';

  // Context-aware back chevron — mirrors the ESC handler above.
  const goBack = () => {
    if (view.mode === 'S6') setView({ mode: view.from || 'S4' });
    else if (view.mode === 'S3') setView({ mode: view.from || 'S0' });
    else if (view.mode === 'S2') setView({ mode: view.from === 'S1' ? 'S1' : 'S0' });
    else if (view.mode === 'S1') setView({ mode: 'S0' });
    else if (view.mode === 'favs' || view.mode === 'lib' || view.mode === 'apple') {
      // Drill-in screens always return to whichever browse surface
      // opened them (S0 or the S4-superset's embedded S0 row).
      setView({ mode: view.from || 'S0' });
    }
    else if (view.mode === 'S4') {
      if (view.from) setView({ mode: view.from });
      else onClose();
    }
    else onClose();
  };

  // PR 3 step 1: dispatch wiring. Per §11.16's room-first rule —
  // when focusRoomId is set, tap-to-play dispatches directly to that
  // room's primary Sonos entity (skipping S3). When focusRoomId is
  // null (content-first via drawer), we'd normally route through S3
  // — but S3 isn't built yet (later steps in this PR), so for now we
  // fall back to a Desk dispatch as a smoke test. The fallback is
  // replaced by the S3 transition once SpeakerRow + PresetTile + S3
  // land in steps 2-4. (`dispatch` itself is declared above the
  // early-return to keep hook-count stable between open/closed.)
  // After play_media settles, skip N tracks forward — used to start
  // an album/playlist from a specific index. Sonos's play_media doesn't
  // support a start-position parameter so we do it the hacky-but-
  // functional way: dispatch the album → wait for it to actually start
  // → fire media_next_track in sequence. ~1.2s settle + ~0.25s per skip.
  const skipForward = async (coordinator, n) => {
    if (!coordinator || !n || n <= 0 || !hass?.callService) return;
    await new Promise((r) => setTimeout(r, 1200));
    for (let i = 0; i < n; i++) {
      try {
        await hass.callService('media_player', 'media_next_track', { entity_id: coordinator });
        await new Promise((r) => setTimeout(r, 250));
      } catch (e) {
        console.warn('[music] skip-to-track failed at index', i, e);
        break;
      }
    }
  };

  // Resume a podcast episode: once the coordinator is actually playing it,
  // seek to Spotify's resume point. Sonos's play_media has no start offset.
  // Waits up to ~10 s for the episode to show as playing; if it never does,
  // it does not seek (seeking whatever else is playing would be worse).
  const seekWhenStarted = async (coordinator, seekMs, itemId) => {
    if (!coordinator || !seekMs || seekMs <= 0 || !hass?.callService) return;
    const isOurs = (st) => {
      if (!st || st.state !== 'playing') return false;
      let cid = st.attributes?.media_content_id || '';
      try { cid = decodeURIComponent(cid); } catch { /* keep raw */ }
      return !itemId || cid.includes(itemId);
    };
    for (let i = 0; i < 20; i++) {
      if (isOurs(hassRef.current?.states?.[coordinator])) {
        try {
          await hass.callService('media_player', 'media_seek', {
            entity_id: coordinator, seek_position: Math.round(seekMs / 1000),
          });
        } catch (e) { console.warn('[music] resume seek failed', e?.message || e); }
        return;
      }
      await new Promise((r) => setTimeout(r, 500));
    }
    console.warn('[music] episode never showed as playing; not seeking', { coordinator, itemId });
  };

  const onPickItem = async (item, opts = {}) => {
    if (!item) return;
    // Tapping a browsable container (artist / playlist / album) opens its track
    // list in S2 instead of playing immediately — the user plays by tapping a
    // track there, which calls back with opts.play and falls through to dispatch
    // below. (Artists never had a "play this" URI; playlists/albums used to play
    // on tap, but the unified model is list-first for Spotify and Apple alike.)
    // opts.open forces the list view for kinds that would otherwise play on
    // tap (a podcast show from the home rows); Liked Songs is always a list.
    if (opts.open || (!opts.play && (item.kind === 'artist' || item.kind === 'playlist' || item.kind === 'album' || item.kind === 'liked'))) {
      setView({ mode: 'S2', item, from: view.mode });
      return;
    }
    if (focusRoomId) {
      // Room-first: dispatch directly to focused room's primary Sonos.
      const focused = rooms.find((r) => r.id === focusRoomId);
      const targetEntity = focused && primarySonosFor(focused);
      if (!targetEntity) {
        console.warn('[music] no primary Sonos for focused room', focusRoomId);
        return;
      }
      const coordinator = await dispatch.dispatchToSpeaker(item, targetEntity);
      if (coordinator) {
        setDispatchedArt(item.imageUrl || null);
        // If opts.startIndex set (track-clicked-inside-album case), skip
        // forward to land on the picked track. Sonos has no start-at-
        // index parameter so we sequence media_next_track calls.
        await skipForward(coordinator, opts.startIndex);
        await seekWhenStarted(coordinator, opts.seekMs, item.id);
        // If opts.queueAfter set (artist view's "Play all" — passes
        // tracks 2..N alongside the first), append them to Sonos's
        // queue with enqueue=add so the user gets all of an artist's
        // top tracks in order, not just the first.
        if (opts.queueAfter?.length) {
          await dispatch.queueTracks(coordinator, opts.queueAfter);
        }
        // Stay at S4 (we're already room-first focused). Just refresh —
        // hero picks up the new track via Sonos state push.
        setView({ mode: 'S4' });
      }
      return;
    }
    // Content-first (no focus): route through S3 (target picker).
    // opts carried through so post-dispatch skip + queueAfter can fire
    // in onS3Dispatch.
    setView({ mode: 'S3', item, opts, from: view.mode });
  };

  // Line-in picker callback. Mirrors onPickItem's room-first / content-
  // first branch: focusRoomId set → dispatch directly to focused room
  // (per §11.16 applied to source selection), no S3. focusRoomId null →
  // route through S3 with an item.kind === 'line-in' shape so the
  // standard target picker handles multi-speaker selection. dispatchLineIn
  // is responsible for select_source → join ordering and amp-listen guard.
  const onLineInPick = (sourceSpeaker) => {
    if (!sourceSpeaker) return;
    setLineInPickerOpen(false);
    const item = {
      kind: 'line-in',
      id: sourceSpeaker.entity,
      title: `${sourceSpeaker.name} · Line-in`,
      subtitle: 'Sonos line input',
      sourceEntity: sourceSpeaker.entity,
      imageUrl: null,
    };
    if (focusRoomId) {
      const focused = rooms.find((r) => r.id === focusRoomId);
      const targetEntity = focused && primarySonosFor(focused);
      if (!targetEntity) {
        console.warn('[music] line-in dispatch — no primary Sonos for focused room', focusRoomId);
        return;
      }
      (async () => {
        const followers = targetEntity === item.sourceEntity ? [] : [targetEntity];
        const coordinator = await dispatch.dispatchLineIn(item.sourceEntity, followers);
        if (coordinator) {
          setDispatchedArt(null);
          setView({ mode: 'S4' });
        }
      })();
      return;
    }
    setView({ mode: 'S3', item, from: view.mode });
  };

  // S3 dispatch callback — fires when user picks a target. Returns
  // user to S0 after a successful dispatch (normal mode) or to S4
  // (extend mode, where they're already focused on a room).
  const onS3Dispatch = async (target) => {
    if (!target) return;
    let coordinator = null;
    if (view.extend) {
      // "Send to other speakers" path — join target into current
      // playback's coordinator. No new content dispatched; the existing
      // audio keeps playing and the picked speaker joins in.
      if (!view.coordinatorEntity) return;
      if (target.kind === 'speaker') {
        coordinator = await dispatch.dispatchExtend(view.coordinatorEntity, [target.entity]);
      } else if (target.kind === 'custom') {
        coordinator = await dispatch.dispatchExtend(view.coordinatorEntity, target.entities);
      }
      if (coordinator) setView({ mode: 'S4' });
      return;
    }
    if (!view.item) return;
    // Line-in dispatch — separate path (select_source + join, not
    // play_media). Source is fixed on view.item; target determines the
    // followers set. Preset.currently_playing joins the active group's
    // members; speaker / custom carry their own entities. Whole-house /
    // pavilion-pool presets are filtered out of S3 for line-in (see
    // states.jsx isLineIn) — they need preset-member expansion that
    // isn't wired in this PR.
    if (view.item.kind === 'line-in') {
      const source = view.item.sourceEntity;
      let followers = [];
      if (target.kind === 'speaker') {
        followers = [target.entity];
      } else if (target.kind === 'preset' && target.preset === 'currently_playing') {
        const coordSp = (speakers || []).find((s) => s.entity === target.coordinator);
        followers = coordSp?.groupMembers || [];
      } else if (target.kind === 'custom') {
        followers = target.entities || [];
      } else {
        console.warn('[music] line-in dispatch — unsupported target kind', target);
        return;
      }
      coordinator = await dispatch.dispatchLineIn(source, followers);
      if (coordinator) {
        setDispatchedArt(null);
        setView({ mode: 'S4', from: 'S0' });
      }
      return;
    }
    if (target.kind === 'preset') {
      coordinator = await dispatch.dispatchToGroup(view.item, target.preset, target.coordinator);
    } else if (target.kind === 'speaker') {
      coordinator = await dispatch.dispatchToSpeaker(view.item, target.entity);
    } else if (target.kind === 'custom') {
      coordinator = await dispatch.dispatchCustom(view.item, target.entities, target.coordinator);
    }
    if (coordinator) {
      setDispatchedArt(view.item.imageUrl || null);
      // Honour the track-index skip when S2 → S3 carried opts.startIndex.
      await skipForward(coordinator, view.opts?.startIndex);
      await seekWhenStarted(coordinator, view.opts?.seekMs, view.item.id);
      // Honour opts.queueAfter (artist "Play all" — tracks 2..N
      // alongside the first); appends them via enqueue=add so the
      // dispatched-target speaker queues the full artist set.
      if (view.opts?.queueAfter?.length) {
        await dispatch.queueTracks(coordinator, view.opts.queueAfter);
      }
      // Land at S4 (focused now-playing takeover) so the user sees
      // confirmation of what just played. `from: 'S0'` so the back
      // chevron returns to S0's browse surface, not closes the shell.
      setView({ mode: 'S4', from: 'S0' });
    }
  };

  // S6 group editor — applies user's checkbox diff via media_player
  // join / unjoin. The S6 component owns the selection state; this
  // handler runs at Save time.
  const onS6Save = async (newSelection, coordinator) => {
    if (!hass?.callService || !coordinator) return;
    const current = new Set(focusRoom?.sonos?.groupMembers || []);
    const next = new Set(newSelection);

    // Coordinator transfer — when the user unchecks the current
    // coordinator AND there's at least one other member to keep, we
    // tear the group down and re-form with a new coordinator. The
    // previous behaviour forced the coordinator to stay, which was a
    // dead-end for the line-in case: dispatching turntable line-in
    // into Pavilion makes Office Turntable (no room view) the
    // coordinator; from Pavilion's S6 the user can't remove Office
    // Turntable because the focus room only renders S6 for itself.
    // Sonos has no "elect new leader" service — rebuilding the group
    // (unjoin all → join into new coord) is the only way. Audio
    // briefly interrupts, which is acceptable since the user
    // explicitly removed the source. New coordinator: prefer the focus
    // room's primary Sonos (matches "I'm in Pavilion, Pavilion should
    // lead" intent); fall back to first remaining member.
    const coordinatorRemoved = !next.has(coordinator);
    if (coordinatorRemoved && next.size > 0) {
      const focusPrimary = primarySonosFor(focusRoom);
      const newCoord = next.has(focusPrimary) ? focusPrimary : [...next][0];
      const followers = [...next].filter((e) => e !== newCoord);
      try {
        await ensureAmpListening(hass, [newCoord, ...followers]);
        // Unjoin every current member (including the old coordinator).
        // Doing it for all members rather than just the coordinator
        // avoids race conditions where Sonos's group state is still in
        // flux while we issue the re-join.
        for (const e of current) {
          await hass.callService('media_player', 'unjoin', { entity_id: e });
        }
        if (followers.length > 0) {
          // Brief settle before re-join — same 400ms tick used in
          // dispatch.jsx for the same reason (Sonos finishes group
          // reconfiguration before play_media / join lands cleanly).
          await new Promise((r) => setTimeout(r, 400));
          await hass.callService('media_player', 'join', {
            entity_id: newCoord,
            group_members: followers,
          });
        }
      } catch (e) {
        console.error('[music] S6 coordinator transfer failed', e);
      }
      setView({ mode: 'S4' });
      return;
    }

    // Normal path: coordinator stays in the group, diff add/remove.
    const toJoin = [...next].filter((e) => !current.has(e));
    const toUnjoin = [...current].filter((e) => !next.has(e));
    try {
      if (toJoin.length > 0) {
        // Amp-listen guard: Pavilion / Rumpus members need their
        // external amp in listening mode or the join lands silent.
        await ensureAmpListening(hass, [coordinator, ...toJoin]);
        await hass.callService('media_player', 'join', {
          entity_id: coordinator,
          group_members: toJoin,
        });
      }
      for (const e of toUnjoin) {
        await hass.callService('media_player', 'unjoin', { entity_id: e });
      }
    } catch (e) {
      console.error('[music] S6 save failed', e);
    }
    setView({ mode: 'S4' });
  };

  // Eyebrow + title vary by view. S0 shows brand mark or focused room;
  // S1 shows "Search"; S2 shows the detail's kind + title; S3 shows
  // "Send to" + content title (or "Extend" in extend mode); S4 shows
  // "Now playing" + room name; S6 shows "Edit group" + room name.
  let eyebrow, title;
  if (view.mode === 'favs') {
    eyebrow = 'Sonos';
    title = 'Favourites';
  } else if (view.mode === 'lib') {
    eyebrow = 'Spotify';
    title = 'Library';
  } else if (view.mode === 'S6') {
    eyebrow = 'Edit group';
    title = focusRoom?.name || 'Group';
  } else if (view.mode === 'S4') {
    eyebrow = 'Now playing';
    title = focusRoom
      ? focusRoom.name
      : 'Wallace St';
  } else if (view.mode === 'S3' && view.extend) {
    eyebrow = 'Extend';
    title = focusRoom?.name || 'Playback';
  } else if (view.mode === 'S3' && view.item) {
    eyebrow = 'Send to';
    title = view.item.title;
  } else if (view.mode === 'S2' && view.item) {
    eyebrow = (view.item.kind || 'detail').toUpperCase();
    title = view.item.title;
  } else if (view.mode === 'S1') {
    eyebrow = 'Search';
    title = 'Spotify';
  } else {
    eyebrow = 'Music';
    title = focusRoom
      ? focusRoom.name
      : 'Wallace St';
  }

  // Search affordance lives in the top bar for S0 and S4 — both are
  // "home"-style surfaces where the user might decide to search.
  // S1 already has its own sticky SearchInput; S2/S3/S6 are focused
  // task views where search would be a distraction.
  const showSearchButton = view.mode === 'S0' || view.mode === 'S4';

  const searchButton = showSearchButton ? (
    <button onClick={() => setView({ mode: 'S1' })} aria-label="Search Spotify" style={{
      width: 48, height: 48, borderRadius: '50%',
      border: 'none', background: T.cardSoft,
      color: T.ink, cursor: 'pointer', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {I.search}
    </button>
  ) : null;

  return (
    <TakeoverShell T={T} viewport={viewport}
      eyebrow={eyebrow} title={title}
      onClose={onClose} onBack={goBack}
      headerAction={searchButton}
      bodyless>
      <main style={{
        padding, display: 'flex', flexDirection: 'column', gap: 16,
      }}>
        {view.mode === 'S0' && (
          <>
            <NowPlayingHero
              T={T} room={focusRoom} viewport={viewport}
              hass={hass}
              sonosCommand={sonosCommand}
              sonosSpeakerCommand={sonosSpeakerCommand}
              mediaPlayer={mediaPlayer}
              poolExtension={poolExtension}
              speakers={speakers}
              fallbackImage={dispatchedArt}/>

            {spotify.ready && (
              <S0 T={T} hass={hass} getToken={spotify.getToken}
                  onPickItem={onPickItem}
                  onPickSource={() => setLineInPickerOpen(true)}
                  onOpenFavourites={() => setView({ mode: 'favs', from: view.mode })}
                  onOpenLibrary={() => setView({ mode: 'lib', filter: 'playlists', from: view.mode })}
                  onOpenApple={() => setView({ mode: 'apple', from: view.mode })}
                  onOpenBuilder={onOpenBuilder ? () => { onClose?.(); onOpenBuilder(); } : undefined}/>
            )}
          </>
        )}

        {/* S4 — focused now-playing takeover. When the focused room's
            Sonos is in a multi-member group, the TuneBalance section
            below makes this functionally S5 (member list + per-member
            volume) — same view, conditional content. Destinations
            chips render for Pavilion/Rumpus (the only rooms with
            amp-zone extensions); Kitchen-chip lock + explainer per
            §11.10. */}
        {view.mode === 'S4' && (
          <>
            <NowPlayingHero
              T={T} room={focusRoom} viewport={viewport}
              hass={hass}
              sonosCommand={sonosCommand}
              sonosSpeakerCommand={sonosSpeakerCommand}
              mediaPlayer={mediaPlayer}
              poolExtension={poolExtension}
              speakers={speakers}
              fallbackImage={dispatchedArt}
              showTuneBalance
              eqCommand={eqCommand}
              onOpenGroup={() => setView({ mode: 'S6', from: 'S4' })}/>

            {!focusRoom && (
              <div style={{
                fontFamily: A_FONT.sans, fontSize: fs(T, 15), fontWeight: 500,
                color: T.inkSoft, padding: '32px 0', textAlign: 'center',
              }}>
                Nothing playing yet.
              </div>
            )}

            {focusRoom && (
              <DestinationsRail T={T} room={focusRoom}
                destinationsCommand={destinationsCommand}/>
            )}

            {focusRoom && (
              <NowPlayingQueue T={T} roomId={focusRoom.id} roomName={focusRoom.name}
                roomEntity={primarySonosFor(focusRoom)}
                hass={hass}
                getSonosQueue={getSonosQueue}
                getToken={spotify.getToken}
                currentContentId={focusRoom?.sonos?.mediaContentId}
                currentTitle={focusRoom?.sonos?.mediaTitle}/>
            )}

            {/* Browse content from S0 — Favourites + Sources (line-in)
                + Recently Played + Made For You. Per user feedback PR4b:
                the room-focused surface should be a superset of S0,
                not a stripped-down view. Search is reachable via the
                top-bar search button (now visible in S4 mode too).
                Dispatch from these tiles goes via room-first rule
                (§11.16) — direct to focusRoom, no S3 detour. */}
            {focusRoom && spotify.ready && (
              <div style={{ paddingTop: 12 }}>
                <S0 T={T} hass={hass} getToken={spotify.getToken}
                    onPickItem={onPickItem}
                    onPickSource={() => setLineInPickerOpen(true)}
                    onOpenFavourites={() => setView({ mode: 'favs', from: 'S4' })}
                    onOpenLibrary={() => setView({ mode: 'lib', filter: 'playlists', from: 'S4' })}
                    onOpenApple={() => setView({ mode: 'apple', from: 'S4' })}
                    onOpenBuilder={onOpenBuilder ? () => { onClose?.(); onOpenBuilder(); } : undefined}/>
              </div>
            )}
          </>
        )}

        {view.mode === 'S6' && focusRoom && (
          <S6Editor T={T} room={focusRoom} speakers={speakers}
            onSave={onS6Save}
            onCancel={() => setView({ mode: view.from || 'S4' })}/>
        )}

        {view.mode === 'favs' && (
          <SFavourites T={T} hass={hass} getToken={spotify.getToken}
            onPickItem={onPickItem}/>
        )}

        {view.mode === 'lib' && spotify.ready && (
          <SLibrary T={T} getToken={spotify.getToken}
            filter={view.filter}
            onFilterChange={(next) => setView({ ...view, filter: next })}
            onPickItem={onPickItem}/>
        )}

        {view.mode === 'apple' && (
          <SApple T={T} hass={hass} getToken={spotify.getToken} onPickItem={onPickItem}/>
        )}

        {view.mode === 'S1' && spotify.ready && (
          <S1 T={T} getToken={spotify.getToken}
              query={searchQuery} onChangeQuery={setSearchQuery}
              onPickItem={(item) => setView({ mode: 'S2', item, from: 'S1' })}/>
        )}

        {view.mode === 'S2' && spotify.ready && view.item && (
          <S2 T={T} getToken={spotify.getToken} item={view.item}
              inRoomFocus={!!focusRoomId}
              onPickItem={(picked, opts) => {
                // S2 calls onPickItem for three intents distinguished by opts:
                //   - opts.startIndex set  → play album/playlist starting at
                //                            that track index (album view)
                //   - opts.play === true   → dispatch this picked item
                //                            directly (artist top-tracks
                //                            tap, "play all" button,
                //                            single-episode tap)
                //   - no opts              → navigation; for tracks that
                //                            means drill into the track's
                //                            album so the user sees the full
                //                            record around it
                if (opts && 'startIndex' in opts) {
                  onPickItem(picked, opts);
                } else if (opts && opts.play === true) {
                  onPickItem(picked, opts);
                } else if (picked.kind === 'album' || picked.kind === 'playlist' || picked.kind === 'show') {
                  // Play-all button → dispatch the whole item.
                  // 'show' routes here so the Play-show CTA dispatches
                  // spotify:show:… (Sonos resumes / picks latest) rather
                  // than navigating away.
                  onPickItem(picked, opts);
                } else {
                  // Navigation: track from search results → see album.
                  setView({ mode: 'S2', item: picked, from: view.from });
                }
              }}/>
        )}

        {view.mode === 'S3' && (view.item || view.extend) && (
          <S3 T={T} item={view.item}
              speakers={speakers} hass={hass}
              extend={!!view.extend}
              coordinatorEntity={view.coordinatorEntity || null}
              onDispatch={onS3Dispatch}
              onBack={goBack}/>
        )}
      </main>

      {lineInPickerOpen && (
        <LineInPicker T={T} hass={hass} speakers={speakers}
          onPick={onLineInPick}
          onCancel={() => setLineInPickerOpen(false)}/>
      )}
    </TakeoverShell>
  );
}

export { MusicShell, NowPlayingStrip, NowPlayingHero };
